import { z } from 'zod';

import { GoogleAnalyticsEventsSchema } from '@nw/shared/config';

export const ValidationSchema = z.object({
  type: z.string().optional(),
  params: z.array(z.union([z.string(), z.number()])).optional(),
  method: z.string().optional(),
  error_message: z.string().optional(),
  eval_prop: z.string().optional(),
  eval_value: z.number().optional()
});

export const SegmentEventFieldsSchema = z.object({
  type: z.string(),
  field: z.string(),
  key: z.string().optional(),
  dependent_field: z.string().optional(),
  extra_info_dependent_field: z.string().optional(),
  dependent_form_field: z.string().optional(),
  match_value: z.string().optional(),
  date_format: z.string().optional(),
  time_format: z.string().optional(),
  dependent_segment_field: z.string().optional(),
  preferredDateFormFieldKey: z.string().optional(),
  preferredSlotFormFieldKey: z.string().optional(),
  default_value: z.union([z.string(), z.boolean(), z.number()]).optional()
});

export const SegmentEventSchema = z.object({
  event_name: z.string(),
  event_fields: z.array(SegmentEventFieldsSchema),
  segment_integration_config_list: z.array(z.string()).optional()
});

export const SuccessSegmentEventsSchema = z.record(
  SegmentEventSchema.optional()
);

export const EventsTypeSchema = z.object({
  should_send_segment_event: z.boolean().optional(),
  segment_events_types: z.record(SegmentEventSchema).optional(),
  google_analytics_events: GoogleAnalyticsEventsSchema.optional()
});

export const TimeLapsedOptionSchema = z.object({
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  value: z.string(),
  max_visibility_time: z.string().optional(),
  max_visibility_time_in_days: z.number().optional(),
  preferred_days: z.array(z.number()).optional()
});

export const DropdownOptionSchema = z.object({
  value: z.string(),
  label: z.string()
});

export const FieldDataSchema = z.object({
  terms_and_conditions_url: z.string().optional()
});

const optionsTypeSchema = z.union([
  z.array(DropdownOptionSchema),
  z.array(z.string()),
  z.record(z.string(), z.array(z.string())),
  z.undefined()
]);

export const DayWiseSlotHoldDatesDetailSchema = z.object({
  date: z.string(),
  reason: z.string().optional(),
  condition: z.string().optional()
});

export const SlotSelectionFieldTimeLapsedOptionSchema = z.object({
  time_value: z.string(),
  max_visibility_time: z.string(),
  preferred_days: z.array(z.number()),
  match_keys: z.array(z.string()).optional(),
  condition: z.string().optional(),
  option_type: z.string().optional(),
  lead_type: z.string().optional(),
  extra_dates_to_show_slot: z.array(z.string()).optional(),
  slot_hold_dates: z.array(z.string()).optional(),
  is_immediate_slot: z.boolean().optional()
});

export const DependentOnOptionConfigsSchema = z.object({
  dependent_on_field_name_enum: z.string(),
  dependent_on_options: z.array(
    z.object({
      dependent_on_field_value: z.string(),
      options: z.array(DropdownOptionSchema)
    })
  )
});

export const FormFieldsSchema = z.object({
  isRelevant: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  relevantCondition: z.string().optional(),
  relevantDependentField: z.string().optional(),
  type: z.string(),
  field: z.string(),
  label: z.string().optional(),
  options: optionsTypeSchema,
  enable_default_suggestion: z.boolean().optional(),
  is_dependent: z.boolean().optional(),
  dependent_fields: z.array(z.string()).optional(),
  dependent_on_field: z.string().optional(),
  search_key: z.string().optional(),
  select_type: z.string().optional(),
  is_tnc: z.boolean().optional(),
  tnc_enum: z.string().optional(),
  validation_type: z.string().optional(),
  regex_type: z.string().optional(),
  validation_message: z.string().optional(),
  validations: z.array(ValidationSchema).optional(),
  fieldAnalysisGaActions: z.array(z.string()).optional(),
  field_data: FieldDataSchema.optional(),
  helper_text: z.string().optional(),
  min_date: z.string().optional(),
  max_date: z.string().optional(),
  filter_date_expression: z.string().optional(),
  computed_string: z.string().optional(),
  should_show_otp_field: z.boolean().optional(),
  should_show_update_option: z.boolean().optional(),
  placeholder: z.string().optional(),
  should_hide_receive_updates: z.boolean().optional(),
  shouldShowLabel: z.boolean().optional(),
  labelHelperText: z.string().optional(),
  should_include_in_create_order: z.boolean().optional(),
  pattern: z.string().optional(),
  s3_upload_bucket_path: z.string().optional(),
  supporting_field: z.string().optional(),
  supporting_field_helper_text: z.string().optional(),
  supporting_placeholder: z.string().optional(),
  placeholder_helper_text: z.string().optional(),
  supporting_placeholder_helper_text: z.string().optional(),
  shouldPrefilledFromUserProfile: z.boolean().optional(),
  shouldCheckAdditionalRelevantCondition: z.boolean().optional(),
  is_expected_removal: z.boolean().optional(),
  isMarginBottomZero: z.boolean().optional(),
  should_show_icon: z.boolean().optional(),
  relevantLabel: z.string().optional(),
  options_type: z.string().optional(),
  time_lapsed_options: z.array(TimeLapsedOptionSchema).optional(),
  show_only_slot_time: z.boolean().optional(),
  should_show_only_label: z.boolean().optional(),
  default_value: z.boolean().optional(),
  mobile_placeholder: z.string().optional(),
  otpInputType: z.string().optional(),
  notificationChannels: z.array(z.string()).optional(),
  slot_options: z.array(SlotSelectionFieldTimeLapsedOptionSchema).optional(),
  intensive_slots_language_options: z.record(z.array(z.string())).optional(),
  academy_slots_language_options: z.record(z.array(z.string())).optional(),
  day_wise_slot_hold_dates: z
    .array(DayWiseSlotHoldDatesDetailSchema)
    .optional(),
  slotConditions: z.record(z.string()).optional(),
  slots_ui_type: z.string().optional(),
  is_date_based_slots: z.boolean().optional(),
  dependent_on_fields: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  conditionally_dependent_values: z
    .array(
      z.object({
        condition: z.string(),
        value: z.union([z.string(), z.boolean()]),
        isConditionalValue: z.boolean().optional()
      })
    )
    .optional(),
  get_otp_button_actions: z.array(z.string()).optional(),
  isCloseAllow: z.boolean().optional(),
  imgLink: z.string().optional(),
  alt_text: z.string().optional(),
  description: z.string().optional(),
  heading: z.string().optional(),
  minimum_select_field_allowed: z.number().optional(),
  initial_select_field_number: z.number().optional(),
  add_select_field_text: z.string().optional(),
  should_show_error_icon: z.boolean().optional(),
  name: z.string().optional(),
  placeholder_text: z.string().optional(),
  state_field_name: z.string().optional(),
  path_to_upload: z.string().optional(),
  acl_type: z.string().optional(),
  max_size_to_upload_in_mb: z.number().optional(),
  dependent_on_option_configs: DependentOnOptionConfigsSchema.optional(),
  should_verify_email: z.boolean().optional(),
  is_required: z.boolean().optional(),
  date_format: z.string().optional(),
  max_visibility_time: z.string().optional(),
  exclude_dates: z.array(z.string()).optional(),
  show_month_year_picker: z.boolean().optional(),
  custom_yup_validation: z.string().optional(),
  hide_radio_buttons: z.boolean().optional(),
  is_customized_radio_options: z.boolean().optional(),
  category: z.string().optional()
});

export const NextStepDetailsSchema = z.object({
  step_id: z.string(),
  btn_text: z.string(),
  btn_id: z.string(),
  btn_actions: z.array(z.string()).optional()
});

export const CalendarInviteCTASchema = z.object({
  btn_id: z.string()
});

export const ExtraHeaderUIDetailsSchema = z.object({
  type: z.string(),
  title: z.string().optional(),
  description: z.string().optional()
});

export const StepInfoSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  step_ui_type: z.string().optional(),
  custom_image_url: z.string().optional(),
  should_show_limited_seat_cta: z.boolean().optional(),
  progress_bar_info: z
    .object({
      type: z.string(),
      total_steps: z.number(),
      current_step: z.number(),
      step_details: z
        .object({
          title: z.string().optional(),
          customStyles: z.string().optional()
        })
        .optional()
    })
    .optional(),
  extra_header_ui_details: ExtraHeaderUIDetailsSchema.optional(),
  extra_footer_ui_details: z
    .object({
      type: z.string(),
      title: z.string(),
      description: z.string()
    })
    .optional(),
  cta_details: z
    .array(
      z.object({
        cta_text: z.string().optional(),
        cta_id: z.string().optional(),
        cta_type: z.string().optional(),
        cta_actions: z.array(z.string()).optional()
      })
    )
    .optional(),
  tnc_text: z.string().optional(),
  calendar_invite_cta_details: CalendarInviteCTASchema.optional(),
  submit_btn_cta_details: z
    .object({
      cta_text: z.string().optional(),
      cta_btn_id: z.string(),
      cta_type: z.enum(['SKIP']).optional(),
      cta_btn_actions: z.array(z.string())
    })
    .optional()
});

export const StepTypeEnum = z.enum([
  'DEFAULT',
  'AUTHENTICATION_STEP',
  'EMBEDDED_SUCCESS_FORM',
  'EMBEDDED_SUCCESS_PAGE',
  'CUSTOM_SUCCESS_PAGE',
  'EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS',
  'WEBINAR_CTA_SUCCESS_PAGE'
]);

const optionalFieldsDataStepTypes = [
  'EMBEDDED_SUCCESS_PAGE',
  'WEBINAR_CTA_SUCCESS_PAGE'
];

export const StepConfigSchema = z
  .object({
    step_id: z.string(),
    step_type: StepTypeEnum,
    next_step_details: NextStepDetailsSchema.optional(),
    previous_step_details: NextStepDetailsSchema.optional(),
    step_info: StepInfoSchema.optional(),
    fields_data: z.array(FormFieldsSchema).optional()
  })
  .refine(
    (data) => {
      if (
        !optionalFieldsDataStepTypes.includes(data.step_type) &&
        data.fields_data === undefined
      ) {
        return false;
      }
      return true;
    },
    {
      message:
        'fields_data is required unless step_type allows it to be optional'
    }
  );

export const MultiStepFormConfigSchema = z.object({
  initial_step_id: z.string(),
  step_details: z.array(StepConfigSchema)
});

export const RequestObjectExtraFieldsDataSchema = z.array(
  z.object({
    field: z.string(),
    tags: z.array(
      z.enum(['NON_PII', 'BASIC_PII', 'INTERMEDIATE_PII', 'CRITICAL_PII'])
    ),
    field_name_in_request_object: z.string().optional()
  })
);

export const MultiStepFormFieldsDataSchema = z.object({
  registration_form_details: MultiStepFormConfigSchema,
  request_object_extra_fields_data: RequestObjectExtraFieldsDataSchema,
  form_type: z.enum(['MULTI_STEP_PRODUCT_REGISTRATION']),
  design_system_type: z.enum(['SHADCN_DESIGN_SYSTEM']).optional()
});

export const WebinarDayWithSlotFieldOptionTypeSchema = z.object({
  start_time: z.string(),
  end_time: z.string(),
  value: z.string(),
  max_visibility_time: z.string(),
  preferred_days: z.array(z.number()),
  match_keys: z.array(z.string()).optional()
});

const PreferredLanguagesEnum = z.enum([
  'ENGLISH',
  'TELUGU',
  'HINDI',
  'TAMIL',
  'KANNADA',
  'MARATHI',
  'MALAYALAM',
  'PUNJABI',
  'BENGALI',
  'ASSAMESE',
  'BODO',
  'DOGRI',
  'GUJARATI',
  'KASHMIRI',
  'KONKANI',
  'MAITHILI',
  'MANIPURI',
  'NEPALI',
  'ODIA',
  'SANSKRIT',
  'SANTALI',
  'SINDHI',
  'URDU'
]);

const PermanentAddressDetailsSchema = z.object({
  door_no_and_street: z.string().optional(),
  city: z.string().optional(),
  district: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  pincode: z.string().optional()
});

export const UserProfileDetailsSchema = z.object({
  user_id: z.string().uuid(),
  phone_number: z.string().optional(),
  country_code: z.string().optional(),
  email: z.string().nullable().optional(),
  profile_pic_url: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  dob: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  is_phone_number_verified: z.boolean().nullable().optional(),
  is_email_verified: z.boolean().nullable().optional(),
  preferred_languages: z.array(PreferredLanguagesEnum).optional(),
  cover_page_url: z.string().nullable().optional(),
  age: z.number().optional(),
  state_of_residence: z.string().optional(),
  occupation: z.string().optional(),
  i_want_to_receive_updates_directly_on_whatsapp: z
    .boolean()
    .nullable()
    .optional(),
  last_name: z.string().nullable().optional(),
  accepted_tnc: z.boolean().optional(),
  pincode: z.string().optional(),
  age_group: z.string().optional(),
  current_city: z.string().optional(),
  current_state: z.string().optional(),
  current_country: z.string().optional(),
  is_first_time_profile_updated: z.boolean().optional(),
  current_district: z.string().optional(),
  recovery_phone_number: z.string().optional(),
  recovery_country_code: z.string().optional(),
  recovery_email: z.string().optional(),
  invite_code: z.string().optional(),
  is_invite_code_entered: z.boolean().optional(),
  created_at: z.string().optional(),
  current_class_or_grade: z.string().optional(),
  degree: z.string().optional(),
  expected_year_of_graduation: z.string().optional(),
  freshchat_restore_id: z.string().optional(),
  whatsapp_mobile_number: z.string().optional(),
  whatsapp_mobile_number_country_code: z.string().optional(),
  higher_education_qualification: z.string().optional(),
  door_no_and_street: z.string().optional(),
  permanent_address_details: PermanentAddressDetailsSchema.optional(),
  have_laptop: z.boolean().nullable().optional(),
  name_on_certificate: z.string().optional(),
  can_update_phone_number: z.boolean().optional(),
  can_update_phone_number_after_datetime: z.string().nullable().optional(),
  nationality: z.string().nullable().optional(),
  family_income: z.string().nullable().optional()
});
